import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { wibToday } from "@/lib/bizdate";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUSES = ["Hadir", "Terlambat", "Pulang Cepat", "Tidak Hadir", "Izin", "Sakit", "Cuti", "Lembur"];
const today = () => wibToday();

export default function HrAttendance() {
  const { can } = useAuth();
  const canManage = can("hr.attendance.manage");
  const canApprove = can("hr.leave.approve");
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [emps, setEmps] = useState([]);
  const [tab, setTab] = useState("harian");
  const [corr, setCorr] = useState([]);
  const [form, setForm] = useState({ employee_id: "", check_in: "", check_out: "", status: "Hadir" });
  const [cf, setCf] = useState(null); // correction form

  const load = useCallback(async () => {
    try {
      setRows((await api.get(`/hr/attendance?date=${date}`)).data);
      setSummary((await api.get(`/hr/attendance/summary?date=${date}`)).data);
    } catch (e) { toast.error(apiError(e)); }
  }, [date]);
  const loadCorr = useCallback(async () => { try { setCorr((await api.get("/hr/attendance/corrections/list")).data); } catch { /* ignore */ } }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCorr(); api.get("/hr/employees?active=true").then((r) => setEmps(r.data)).catch(() => {}); }, [loadCorr]);

  const save = async () => {
    if (!form.employee_id) return toast.error("Pilih karyawan");
    try { await api.post("/hr/attendance", { ...form, date }); toast.success("Absensi tersimpan"); setForm({ employee_id: "", check_in: "", check_out: "", status: "Hadir" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reqCorrection = async () => {
    if (!cf.reason?.trim()) return toast.error("Alasan wajib diisi");
    try { await api.post(`/hr/attendance/${cf.aid}/correction-request`, { new_check_in: cf.check_in || null, new_check_out: cf.check_out || null, new_status: cf.status || null, reason: cf.reason }); toast.success("Pengajuan koreksi terkirim"); setCf(null); loadCorr(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const actCorr = async (cid, path) => { try { await api.post(`/hr/attendance/corrections/${cid}/${path}`, path === "reject" ? { reason: prompt("Alasan tolak:") ?? "" } : {}); toast.success("Diproses"); loadCorr(); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="p-6" data-testid="hr-attendance-page">
      <div className="flex justify-between items-center mb-4">
        <div><h1 className="text-2xl font-semibold text-white">Absensi</h1>
          <p className="text-slate-400 text-sm">Kehadiran harian, keterlambatan (dari Shift), koreksi via persetujuan.</p></div>
        <div className="flex gap-2">
          <button data-testid="tab-harian" onClick={() => setTab("harian")} className={`px-3 py-2 rounded text-sm ${tab === "harian" ? "bg-indigo-600 text-white" : "bg-slate-800 text-slate-300"}`}>Absensi Harian</button>
          <button data-testid="tab-koreksi" onClick={() => setTab("koreksi")} className={`px-3 py-2 rounded text-sm ${tab === "koreksi" ? "bg-indigo-600 text-white" : "bg-slate-800 text-slate-300"}`}>Koreksi {corr.filter((c) => c.status === "Pending").length ? `(${corr.filter((c) => c.status === "Pending").length})` : ""}</button>
        </div>
      </div>

      {tab === "harian" && (
        <>
          <div className="flex items-end gap-3 mb-4">
            <div><label className="text-xs text-slate-400">Tanggal</label><Input data-testid="att-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-slate-800 border-slate-700 text-white w-44" /></div>
          </div>
          {summary && (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
              {STATUSES.map((s) => <Card key={s} className="p-2 bg-slate-900 border-slate-800"><div className="text-xs text-slate-400">{s}</div><div className="text-lg font-semibold text-white">{summary.counts[s] || 0}</div></Card>)}
            </div>
          )}
          {canManage && (
            <Card className="p-4 bg-slate-900 border-slate-800 mb-4" data-testid="att-form">
              <div className="flex flex-wrap items-end gap-3">
                <div><label className="text-xs text-slate-400">Karyawan</label>
                  <select data-testid="att-employee" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="block bg-slate-800 border border-slate-700 rounded px-2 py-2 text-sm text-white w-56"><option value="">Pilih…</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.employee_id} · {e.nama_lengkap}</option>)}</select></div>
                <div><label className="text-xs text-slate-400">Check In</label><Input type="time" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} className="bg-slate-800 border-slate-700 text-white w-28" /></div>
                <div><label className="text-xs text-slate-400">Check Out</label><Input type="time" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} className="bg-slate-800 border-slate-700 text-white w-28" /></div>
                <div><label className="text-xs text-slate-400">Status</label><select data-testid="att-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="block bg-slate-800 border border-slate-700 rounded px-2 py-2 text-sm text-white">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
                <Button data-testid="save-att-btn" onClick={save} className="bg-emerald-600 hover:bg-emerald-500">Simpan</Button>
              </div>
            </Card>
          )}
          <Card className="p-0 bg-slate-900 border-slate-800 overflow-x-auto">
            <table className="w-full text-sm text-slate-300">
              <thead><tr className="text-left text-slate-500 border-b border-slate-800"><th className="p-3">NIK</th><th>Nama</th><th>Masuk</th><th>Pulang</th><th>Status</th><th>Telat</th><th>Aksi</th></tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-slate-500">Belum ada absensi pada tanggal ini.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`att-row-${r.employee_code}`} className="border-b border-slate-800">
                    <td className="p-3">{r.employee_code}</td><td>{r.employee_name}</td><td>{r.check_in || "-"}</td><td>{r.check_out || "-"}</td>
                    <td><span className="text-xs px-2 py-1 rounded bg-slate-700/40">{r.status}</span></td>
                    <td>{r.late_minutes ? `${r.late_minutes} mnt` : "-"}</td>
                    <td>{canManage && <button data-testid={`corr-${r.employee_code}`} onClick={() => setCf({ aid: r.id, check_in: r.check_in || "", check_out: r.check_out || "", status: r.status, reason: "" })} className="text-xs text-indigo-400">koreksi</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {tab === "koreksi" && (
        <Card className="p-0 bg-slate-900 border-slate-800 overflow-x-auto" data-testid="corrections-list">
          <table className="w-full text-sm text-slate-300">
            <thead><tr className="text-left text-slate-500 border-b border-slate-800"><th className="p-3">Tanggal</th><th>NIK</th><th>Sebelum</th><th>Sesudah</th><th>Alasan</th><th>Status</th><th>Aksi</th></tr></thead>
            <tbody>
              {corr.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-slate-500">Belum ada koreksi.</td></tr>}
              {corr.map((c) => (
                <tr key={c.id} data-testid={`corr-row-${c.id}`} className="border-b border-slate-800">
                  <td className="p-3">{c.date}</td><td>{c.employee_code}</td>
                  <td className="text-xs">{c.before.check_in || "-"}/{c.before.check_out || "-"} {c.before.status}</td>
                  <td className="text-xs">{c.after.check_in || "-"}/{c.after.check_out || "-"} {c.after.status || c.after.new_status || "-"}</td>
                  <td className="text-xs max-w-[160px] truncate">{c.reason}</td>
                  <td><span className={`text-xs px-2 py-1 rounded ${c.status === "Approved" ? "bg-emerald-500/15 text-emerald-400" : c.status === "Rejected" ? "bg-rose-500/15 text-rose-400" : "bg-amber-500/15 text-amber-400"}`}>{c.status}</span></td>
                  <td>{c.status === "Pending" && canApprove && <span className="space-x-2"><button data-testid={`approve-corr-${c.id}`} onClick={() => actCorr(c.id, "approve")} className="text-xs text-emerald-400">setujui</button><button onClick={() => actCorr(c.id, "reject")} className="text-xs text-rose-400">tolak</button></span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {cf && (
        <Card className="p-4 bg-slate-900 border-slate-800 mt-4" data-testid="correction-form">
          <div className="text-white font-medium mb-2">Ajukan Koreksi Absensi</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
            <div><label className="text-xs text-slate-400">Check In</label><Input type="time" value={cf.check_in} onChange={(e) => setCf({ ...cf, check_in: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
            <div><label className="text-xs text-slate-400">Check Out</label><Input type="time" value={cf.check_out} onChange={(e) => setCf({ ...cf, check_out: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
            <div><label className="text-xs text-slate-400">Status</label><select value={cf.status} onChange={(e) => setCf({ ...cf, status: e.target.value })} className="block bg-slate-800 border border-slate-700 rounded px-2 py-2 text-sm text-white">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
            <div><label className="text-xs text-slate-400">Alasan *</label><Input data-testid="corr-reason" value={cf.reason} onChange={(e) => setCf({ ...cf, reason: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
          </div>
          <div className="mt-3 flex gap-2"><Button data-testid="submit-corr-btn" onClick={reqCorrection} className="bg-emerald-600 hover:bg-emerald-500">Kirim Pengajuan</Button><Button onClick={() => setCf(null)} className="bg-slate-700 hover:bg-slate-600">Batal</Button></div>
        </Card>
      )}
    </div>
  );
}
